import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { payments, members } from '../../utils/stubData';

function Payments() {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    paymentMethod: '',
    year: new Date().getFullYear().toString(),
    dateFrom: '',
    dateTo: '',
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'paymentDate',
    direction: 'desc',
  });

  const [selectedPayments, setSelectedPayments] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSort = key => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const togglePaymentSelection = paymentId => {
    setSelectedPayments(prev =>
      prev.includes(paymentId) ? prev.filter(id => id !== paymentId) : [...prev, paymentId]
    );
  };

  const toggleAllPayments = () => {
    if (selectedPayments.length === filteredPayments.length) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(filteredPayments.map(p => p.id));
    }
  };

  const getMemberInfo = membershipNumber => {
    return members.find(m => m.membershipNumber === membershipNumber);
  };

  const filteredPayments = payments
    .filter(payment => {
      const member = getMemberInfo(payment.membershipNumber);
      const searchMatch =
        !filters.search ||
        payment.transactionId?.toLowerCase().includes(filters.search.toLowerCase()) ||
        payment.receiptNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        payment.membershipNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
        (member &&
          (member.firstName.toLowerCase().includes(filters.search.toLowerCase()) ||
            member.lastName.toLowerCase().includes(filters.search.toLowerCase())));

      const statusMatch = !filters.status || payment.status === filters.status;
      const methodMatch = !filters.paymentMethod || payment.paymentMethod === filters.paymentMethod;
      const yearMatch = !filters.year || payment.year.toString() === filters.year;

      let dateMatch = true;
      if (filters.dateFrom) {
        dateMatch = dateMatch && new Date(payment.paymentDate) >= new Date(filters.dateFrom);
      }
      if (filters.dateTo) {
        dateMatch = dateMatch && new Date(payment.paymentDate) <= new Date(filters.dateTo);
      }

      return searchMatch && statusMatch && methodMatch && yearMatch && dateMatch;
    })
    .sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const SortHeader = ({ label, sortKey }) => (
    <th
      className="px-4 py-2 cursor-pointer hover:bg-gray-50 text-hdlcc-textDark text-left"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {sortConfig.key === sortKey && (
          <span className="text-hdlcc-accentRed">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );

  const totalAmount = filteredPayments
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.amount, 0);
  const pendingAmount = filteredPayments
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + p.amount, 0);
  const failedAmount = filteredPayments
    .filter(p => p.status === 'failed')
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">Payments</h2>
          <p className="text-hdlcc-textMuted">Track and manage all payments</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-gray-100 text-hdlcc-textDark px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition-colors"
          >
            Export Payments
          </button>
          <Link
            to="/admin/payments/manual"
            className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
          >
            Record Manual Payment
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Total Completed</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">£{totalAmount.toFixed(2)}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">
            {filteredPayments.filter(p => p.status === 'completed').length} payments
          </p>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Pending</h4>
          <p className="text-2xl font-bold mt-2 text-yellow-400">£{pendingAmount.toFixed(2)}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">
            {filteredPayments.filter(p => p.status === 'pending').length} payments
          </p>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Failed</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-accentRed">£{failedAmount.toFixed(2)}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">
            {filteredPayments.filter(p => p.status === 'failed').length} payments
          </p>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">This Year</h4>
          <p className="text-2xl font-bold mt-2 text-hdlcc-textDark">
            £
            {payments
              .filter(p => p.year === new Date().getFullYear() && p.status === 'completed')
              .reduce((sum, p) => sum + p.amount, 0)
              .toFixed(2)}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">All completed payments</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label htmlFor="payments-search" className="block text-sm text-hdlcc-textMuted mb-1">
              Search
            </label>
            <input
              id="payments-search"
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Receipt, transaction..."
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="payments-status" className="block text-sm text-hdlcc-textMuted mb-1">
              Status
            </label>
            <select
              id="payments-status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="payments-payment-method"
              className="block text-sm text-hdlcc-textMuted mb-1"
            >
              Payment Method
            </label>
            <select
              id="payments-payment-method"
              name="paymentMethod"
              value={filters.paymentMethod}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Methods</option>
              <option value="stripe">Stripe (Card)</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>

          <div>
            <label htmlFor="payments-year" className="block text-sm text-hdlcc-textMuted mb-1">
              Year
            </label>
            <select
              id="payments-year"
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            >
              <option value="">All Years</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
            </select>
          </div>

          <div>
            <label htmlFor="payments-date-from" className="block text-sm text-hdlcc-textMuted mb-1">
              Date From
            </label>
            <input
              id="payments-date-from"
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>

          <div>
            <label htmlFor="payments-date-to" className="block text-sm text-hdlcc-textMuted mb-1">
              Date To
            </label>
            <input
              id="payments-date-to"
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleFilterChange}
              className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
            />
          </div>
        </div>

        {selectedPayments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
            <span className="text-sm text-hdlcc-textDark">
              {selectedPayments.length} payment{selectedPayments.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-2">
              <button className="bg-gray-100 text-hdlcc-textDark px-3 py-1 rounded hover:bg-gray-50 border border-gray-300 text-sm transition-colors">
                Export Selected
              </button>
              <button className="bg-gray-100 text-hdlcc-textDark px-3 py-1 rounded hover:bg-gray-50 border border-gray-300 text-sm transition-colors">
                Send Receipts
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payments Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-hdlcc-bgWhite border border-gray-200 rounded-lg overflow-hidden shadow-card">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="px-4 py-2 text-hdlcc-textDark">
                <input
                  type="checkbox"
                  checked={
                    selectedPayments.length === filteredPayments.length &&
                    filteredPayments.length > 0
                  }
                  onChange={toggleAllPayments}
                  className="rounded"
                />
              </th>
              <SortHeader label="Receipt No." sortKey="receiptNumber" />
              <SortHeader label="Member" sortKey="membershipNumber" />
              <SortHeader label="Amount" sortKey="amount" />
              <SortHeader label="Date" sortKey="paymentDate" />
              <SortHeader label="Method" sortKey="paymentMethod" />
              <SortHeader label="Status" sortKey="status" />
              <SortHeader label="Year" sortKey="year" />
              <th className="px-4 py-2 text-hdlcc-textDark text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map(payment => {
              const member = getMemberInfo(payment.membershipNumber);
              return (
                <tr
                  key={payment.id}
                  className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={selectedPayments.includes(payment.id)}
                      onChange={() => togglePaymentSelection(payment.id)}
                      onClick={e => e.stopPropagation()}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-hdlcc-textDark">
                    {payment.receiptNumber || 'Pending'}
                  </td>
                  <td className="px-4 py-2">
                    {member ? (
                      <Link
                        to={`/admin/members/${payment.membershipNumber}`}
                        className="text-hdlcc-accentRed hover:text-red-600"
                      >
                        {member.firstName} {member.lastName}
                        <br />
                        <span className="text-xs text-hdlcc-textMuted">
                          {payment.membershipNumber}
                        </span>
                      </Link>
                    ) : (
                      <span className="text-hdlcc-textMuted">New Application</span>
                    )}
                  </td>
                  <td className="px-4 py-2 font-semibold text-hdlcc-textDark">
                    £{payment.amount.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-hdlcc-textDark">
                    {new Date(payment.paymentDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 capitalize text-hdlcc-textDark">
                    {payment.paymentMethod.replace('_', ' ')}
                    {payment.isManual && (
                      <span className="text-xs text-yellow-400 ml-2">(Manual)</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        payment.status === 'completed'
                          ? 'bg-green-900 text-green-200 border border-green-500'
                          : payment.status === 'pending'
                            ? 'bg-yellow-900 text-yellow-200 border border-yellow-500'
                            : payment.status === 'failed'
                              ? 'bg-red-900 text-red-200 border border-red-500'
                              : 'bg-gray-700 text-gray-200 border border-gray-500'
                      }`}
                    >
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-hdlcc-textDark">{payment.year}</td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      <button
                        className="text-hdlcc-accentRed hover:text-red-600 text-sm"
                        title="View Receipt"
                      >
                        View
                      </button>
                      {payment.status === 'completed' && (
                        <button
                          className="text-hdlcc-accentRed hover:text-red-600 text-sm"
                          title="Send Receipt"
                        >
                          Send
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredPayments.length === 0 && (
        <div className="text-center py-12 text-hdlcc-textMuted">
          <p>No payments found matching your filters.</p>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-hdlcc-bgWhite p-6 rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            <h3 className="text-xl font-bold mb-4 text-hdlcc-textDark">Export Payments</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="export-format" className="block text-sm text-hdlcc-textMuted mb-2">
                  Export Format
                </label>
                <select
                  id="export-format"
                  className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300"
                >
                  <option>CSV</option>
                  <option>Excel (XLSX)</option>
                  <option>PDF</option>
                </select>
              </div>
              <div>
                <label
                  htmlFor="export-date-range"
                  className="block text-sm text-hdlcc-textMuted mb-2"
                >
                  Date Range
                </label>
                <select
                  id="export-date-range"
                  className="w-full p-2 border rounded bg-white text-hdlcc-textDark border-gray-300"
                >
                  <option>Current Filters</option>
                  <option>This Month</option>
                  <option>Last Month</option>
                  <option>This Year</option>
                  <option>Last Year</option>
                  <option>All Time</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-2 mt-6">
              <button className="flex-1 bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
                Export
              </button>
              <button
                onClick={() => setShowExportModal(false)}
                className="flex-1 bg-gray-100 text-hdlcc-textDark px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
